// packages/project-types/website/index.ts — Website project type.
// Multi-page marketing site with SEO metadata, responsive sections,
// asset manager integration, and Cloudflare Pages publish target.
import type { ProjectType, ScaffoldInput, FileTree } from '../types';

// ---------------------------------------------------------------------------
// Section templates
// ---------------------------------------------------------------------------

function heroSection(siteName: string): string {
  return `<section class="section-hero">
  <div class="container">
    <h1>${siteName}</h1>
    <p class="lead">Your tagline goes here.</p>
    <a href="#contact" class="btn btn-primary">Get started</a>
  </div>
</section>`;
}

function featuresSection(): string {
  return `<section class="section-features">
  <div class="container">
    <h2>Features</h2>
    <div class="features-grid">
      <div class="feature">
        <h3>Feature one</h3>
        <p>Description of this feature.</p>
      </div>
      <div class="feature">
        <h3>Feature two</h3>
        <p>Description of this feature.</p>
      </div>
      <div class="feature">
        <h3>Feature three</h3>
        <p>Description of this feature.</p>
      </div>
    </div>
  </div>
</section>`;
}

function ctaSection(): string {
  return `<section class="section-cta">
  <div class="container">
    <h2>Ready to get started?</h2>
    <p>Join thousands of satisfied customers.</p>
    <a href="#contact" class="btn btn-primary">Contact us</a>
  </div>
</section>`;
}

function footerSection(siteName: string): string {
  return `<footer class="site-footer">
  <div class="container">
    <p>&copy; ${new Date().getFullYear()} ${siteName}. All rights reserved.</p>
  </div>
</footer>`;
}

function navSection(siteName: string, pages: string[]): string {
  const links = pages
    .map((p) => `    <a href="${p === 'index' ? '/' : `/${p}`}">${capitalize(p)}</a>`)
    .join('\n');
  return `<nav class="site-nav">
  <div class="container">
    <a href="/" class="nav-brand">${siteName}</a>
    <div class="nav-links">
${links}
    </div>
  </div>
</nav>`;
}

// ---------------------------------------------------------------------------
// Page builders
// ---------------------------------------------------------------------------

function buildIndexPage(siteName: string, pages: string[], accent: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${siteName}</title>
  <meta name="description" content="${siteName} — your description here." />
  <meta property="og:title" content="${siteName}" />
  <meta property="og:type" content="website" />
  <link rel="stylesheet" href="/styles.css" />
</head>
<body>
${navSection(siteName, pages)}
<main>
${heroSection(siteName)}
${featuresSection()}
${ctaSection()}
</main>
${footerSection(siteName)}
</body>
</html>
`;
}

function buildGenericPage(siteName: string, slug: string, pages: string[]): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${capitalize(slug)} | ${siteName}</title>
  <meta name="description" content="${capitalize(slug)} page for ${siteName}." />
  <link rel="stylesheet" href="/styles.css" />
</head>
<body>
${navSection(siteName, pages)}
<main>
  <section class="section-page">
    <div class="container">
      <h1>${capitalize(slug)}</h1>
      <p>Content for the ${slug} page goes here.</p>
    </div>
  </section>
</main>
${footerSection(siteName)}
</body>
</html>
`;
}

function buildStyles(accent: string): string {
  return `/* Generated by AI Builder — website project */
:root {
  --color-accent: ${accent};
  --color-text:   #1a1a2e;
  --color-bg:     #ffffff;
  --color-bg-alt: #f8f9fa;
  --font-sans:    system-ui, -apple-system, sans-serif;
  --radius:       8px;
  --max-w:        1200px;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { font-size: 16px; scroll-behavior: smooth; }
body { font-family: var(--font-sans); color: var(--color-text); background: var(--color-bg); line-height: 1.6; }

.container { max-width: var(--max-w); margin: 0 auto; padding: 0 1.5rem; }

/* Nav */
.site-nav { border-bottom: 1px solid #e5e7eb; padding: 1rem 0; }
.site-nav .container { display: flex; align-items: center; justify-content: space-between; }
.nav-brand { font-weight: 700; color: var(--color-text); text-decoration: none; font-size: 1.125rem; }
.nav-links { display: flex; gap: 1.5rem; }
.nav-links a { color: var(--color-text); text-decoration: none; font-size: 0.9375rem; opacity: 0.8; }
.nav-links a:hover { opacity: 1; }

/* Sections */
.section-hero { padding: 6rem 0; text-align: center; background: var(--color-bg-alt); }
.section-hero h1 { font-size: clamp(2rem, 5vw, 3.5rem); font-weight: 800; margin-bottom: 1rem; }
.section-hero .lead { font-size: 1.25rem; opacity: 0.7; margin-bottom: 2rem; }
.section-features { padding: 5rem 0; }
.section-features h2 { text-align: center; font-size: 2rem; margin-bottom: 3rem; }
.features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 2rem; }
.feature { padding: 1.5rem; border: 1px solid #e5e7eb; border-radius: var(--radius); }
.feature h3 { font-size: 1.125rem; margin-bottom: 0.5rem; }
.section-cta { padding: 5rem 0; text-align: center; background: var(--color-bg-alt); }
.section-cta h2 { font-size: 2rem; margin-bottom: 1rem; }
.section-cta p { opacity: 0.7; margin-bottom: 2rem; }
.section-page { padding: 5rem 0; }
.section-page h1 { font-size: 2.25rem; margin-bottom: 1rem; }

/* Button */
.btn { display: inline-block; padding: 0.75rem 1.75rem; border-radius: var(--radius); font-weight: 600; text-decoration: none; cursor: pointer; transition: opacity 0.18s; }
.btn-primary { background: var(--color-accent); color: #fff; }
.btn-primary:hover { opacity: 0.88; }

/* Footer */
.site-footer { border-top: 1px solid #e5e7eb; padding: 2rem 0; text-align: center; font-size: 0.875rem; opacity: 0.7; }

@media (max-width: 768px) {
  .nav-links { gap: 1rem; }
  .section-hero { padding: 4rem 0; }
}
`;
}

function buildMetadataJson(siteName: string, slug: string, pages: string[]): string {
  return JSON.stringify(
    {
      siteName,
      slug,
      pages: pages.map((p) => ({
        slug: p,
        title: p === 'index' ? siteName : `${capitalize(p)} | ${siteName}`,
        description: `${capitalize(p === 'index' ? siteName : p)} page.`,
        og: { title: p === 'index' ? siteName : `${capitalize(p)} | ${siteName}`, type: 'website' },
        canonical: p === 'index' ? '/' : `/${p}`,
        schemaOrg: { '@context': 'https://schema.org', '@type': 'WebPage', name: siteName },
      })),
    },
    null,
    2,
  );
}

function buildWranglerToml(slug: string): string {
  return `# Cloudflare Pages publish config
name = "${slug}"
compatibility_date = "2024-01-01"
pages_build_output_dir = "."
`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}

// ---------------------------------------------------------------------------
// Project type definition
// ---------------------------------------------------------------------------

export const website: ProjectType = {
  id:          'website',
  label:       'Website',
  description: 'Multi-page marketing site with SEO, sections, and Cloudflare Pages publishing.',
  icon:        '🌐',

  scaffold(input: ScaffoldInput): FileTree {
    const pages  = input.pages?.length ? input.pages : ['index', 'about', 'contact'];
    const accent = input.accentColor ?? '#6366f1';
    const tree: FileTree = {};

    // HTML pages
    for (const page of pages) {
      const filename = page === 'index' ? 'index.html' : `${page}.html`;
      tree[filename] = page === 'index'
        ? buildIndexPage(input.projectName, pages, accent)
        : buildGenericPage(input.projectName, page, pages);
    }

    // Shared stylesheet
    tree['styles.css'] = buildStyles(accent);

    // SEO metadata manifest
    tree['metadata.json'] = buildMetadataJson(input.projectName, input.projectSlug, pages);

    // Publish config
    tree['wrangler.toml'] = buildWranglerToml(input.projectSlug);

    // README
    tree['README.md'] = `# ${input.projectName}\n\n${input.description ?? 'Website project.'}\n\n## Pages\n${pages.map((p) => `- \`${p === 'index' ? 'index.html' : p + '.html'}\``).join('\n')}\n\n## Publish\n\nDeploy to Cloudflare Pages via the Publish tab.\n`;

    return tree;
  },

  defaultVerificationMatrix: [
    'lint',
    'typecheck',
    'secretScan',
    'playwrightRuntime',
    'screenshotDiff',
  ],

  defaultApprovalPolicy: {
    alwaysApprove: ['publish.live'],
  },

  screens: ['preview', 'code', 'files', 'tests', 'visualqa', 'split'],
};
